import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  X,
  Search,
  Users,
} from "lucide-react";
import { scenariosAPI } from "../../services/api";

/**
 * Per-agent metric selection for a scenario. One collapsible card per agent;
 * each agent shows its own applicable metrics with simulation defaults
 * pre-checked. Value shape: { "<agentId>": [metricId, ...] }.
 *
 * Props:
 *   agents     [{ id, name }]  full workspace agent list (for the add dropdown)
 *   value      { agentId: [metricIds] }
 *   onChange   (nextMap) => void
 *   clientId   workspace id
 *   disabled   bool
 *   autoAddAgentId  agent to auto-add (with its defaults) on first mount when
 *                   the value is empty — used to pre-select the current agent
 */
const AgentMetricsPicker = ({
  agents = [],
  value = {},
  onChange,
  clientId,
  disabled = false,
  autoAddAgentId = null,
}) => {
  const [optionsByAgent, setOptionsByAgent] = useState({}); // { agentId: [{id,name,default,scope}] }
  const [loading, setLoading] = useState({}); // { agentId: bool }
  const [expanded, setExpanded] = useState({}); // { agentId: bool }
  const [search, setSearch] = useState({}); // { agentId: query }
  const [addOpen, setAddOpen] = useState(false);

  const addedIds = Object.keys(value).map(Number);
  const agentName = useCallback(
    (id) => agents.find((a) => a.id === Number(id))?.name || `Agent ${id}`,
    [agents],
  );

  const fetchOptions = useCallback(
    async (agentId) => {
      if (!clientId) return null;
      setLoading((l) => ({ ...l, [agentId]: true }));
      try {
        const res = await scenariosAPI.getAgentMetricOptions(agentId, clientId);
        setOptionsByAgent((o) => ({ ...o, [agentId]: res.metrics || [] }));
        return res;
      } catch (e) {
        setOptionsByAgent((o) => ({ ...o, [agentId]: [] }));
        return null;
      } finally {
        setLoading((l) => ({ ...l, [agentId]: false }));
      }
    },
    [clientId],
  );

  // Ensure options are loaded for every agent already in the map (e.g. on edit).
  useEffect(() => {
    addedIds.forEach((id) => {
      if (optionsByAgent[id] === undefined && !loading[id]) fetchOptions(id);
    });
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const addAgent = async (agentId) => {
    setAddOpen(false);
    const res = await fetchOptions(agentId);
    const defaults = res?.default_metric_ids || [];
    onChange({ ...value, [agentId]: defaults });
  };

  // Auto-add the current agent (with its defaults) once, for a fresh scenario.
  const didAutoAdd = useRef(false);
  useEffect(() => {
    if (didAutoAdd.current || !autoAddAgentId) return;
    if (Object.keys(value).length > 0) {
      didAutoAdd.current = true;
      return;
    }
    if (!agents.some((a) => a.id === Number(autoAddAgentId))) return;
    didAutoAdd.current = true;
    addAgent(Number(autoAddAgentId));
  }, [autoAddAgentId, agents]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeAgent = (agentId) => {
    const next = { ...value };
    delete next[agentId];
    onChange(next);
  };

  const setAgentMetrics = (agentId, ids) =>
    onChange({ ...value, [agentId]: ids });

  const toggleMetric = (agentId, metricId) => {
    const cur = value[agentId] || [];
    setAgentMetrics(
      agentId,
      cur.includes(metricId)
        ? cur.filter((m) => m !== metricId)
        : [...cur, metricId],
    );
  };

  const available = agents.filter((a) => !addedIds.includes(a.id));

  return (
    <div className="space-y-3">
      {/* Add agent */}
      <div className="relative inline-block">
        <button
          type="button"
          disabled={disabled || available.length === 0}
          onClick={() => setAddOpen((o) => !o)}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 disabled:opacity-40 disabled:hover:text-primary"
        >
          <Plus className="size-3.5" />{" "}
          {addedIds.length === 0 ? "Select an agent to measure" : "Select more agents"}
        </button>
        {addOpen && available.length > 0 && (
          <div className="absolute z-20 mt-1 max-h-60 w-56 overflow-y-auto rounded-lg border border-border bg-card py-1 shadow-lg">
            {available.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => addAgent(a.id)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
              >
                <Users className="size-3.5 text-muted-foreground" />
                <span className="truncate text-foreground">{a.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {addedIds.length === 0 && (
        <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          Select an agent to choose which metrics measure this scenario.
        </p>
      )}

      {/* Agent cards */}
      {addedIds.map((agentId) => {
        const opts = optionsByAgent[agentId] || [];
        const selected = value[agentId] || [];
        const isOpen = !!expanded[agentId];
        const q = (search[agentId] || "").trim().toLowerCase();
        const filtered = q
          ? opts.filter((o) => o.name.toLowerCase().includes(q))
          : opts;
        const defaults = filtered.filter((o) => o.default);
        const others = filtered.filter((o) => !o.default);
        const selectedNames = selected
          .map((id) => opts.find((o) => o.id === id)?.name)
          .filter(Boolean);

        const renderMetric = (o) => (
          <label
            key={o.id}
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
          >
            <input
              type="checkbox"
              checked={selected.includes(o.id)}
              onChange={() => toggleMetric(agentId, o.id)}
              className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
            />
            <span className="truncate text-foreground">{o.name}</span>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
              {o.scope}
            </span>
          </label>
        );

        return (
          <div
            key={agentId}
            className="rounded-lg border border-border bg-card"
          >
            {/* header */}
            <div className="flex items-center gap-2 px-3 py-2.5">
              <button
                type="button"
                onClick={() =>
                  setExpanded((e) => ({ ...e, [agentId]: !e[agentId] }))
                }
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                {isOpen ? (
                  <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                )}
                <span className="truncate text-sm font-medium text-foreground">
                  {agentName(agentId)}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  · {selected.length} metric{selected.length === 1 ? "" : "s"}
                </span>
                {!isOpen && selectedNames.length > 0 && (
                  <span className="hidden truncate text-xs text-muted-foreground sm:inline">
                    — {selectedNames.slice(0, 3).join(", ")}
                    {selectedNames.length > 3
                      ? ` +${selectedNames.length - 3}`
                      : ""}
                  </span>
                )}
              </button>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeAgent(agentId)}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  title="Remove agent"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>

            {/* body */}
            {isOpen && (
              <div className="border-t border-border p-3">
                {loading[agentId] ? (
                  <div className="py-4 text-center text-sm text-muted-foreground">
                    Loading metrics…
                  </div>
                ) : opts.length === 0 ? (
                  <div className="py-4 text-center text-sm text-muted-foreground">
                    No metrics available for this agent.
                  </div>
                ) : (
                  <>
                    <div className="mb-2 flex items-center gap-2">
                      <div className="relative flex-1">
                        <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          value={search[agentId] || ""}
                          onChange={(e) =>
                            setSearch((s) => ({
                              ...s,
                              [agentId]: e.target.value,
                            }))
                          }
                          placeholder="Search metrics…"
                          className="w-full rounded-md border border-input bg-background py-1.5 pl-7 pr-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div className="flex shrink-0 items-center gap-1 text-xs">
                        <button
                          type="button"
                          onClick={() =>
                            setAgentMetrics(
                              agentId,
                              opts.filter((o) => o.default).map((o) => o.id),
                            )
                          }
                          className="rounded px-2 py-1 font-medium text-primary hover:bg-primary/5"
                        >
                          Defaults
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setAgentMetrics(agentId, opts.map((o) => o.id))
                          }
                          className="rounded px-2 py-1 font-medium text-muted-foreground hover:bg-muted"
                        >
                          All
                        </button>
                        <button
                          type="button"
                          onClick={() => setAgentMetrics(agentId, [])}
                          className="rounded px-2 py-1 font-medium text-muted-foreground hover:bg-muted"
                        >
                          None
                        </button>
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto">
                      {defaults.length > 0 && (
                        <>
                          <div className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Simulation defaults
                          </div>
                          {defaults.map(renderMetric)}
                        </>
                      )}
                      {others.length > 0 && (
                        <>
                          <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Other available
                          </div>
                          {others.map(renderMetric)}
                        </>
                      )}
                      {filtered.length === 0 && (
                        <div className="px-2 py-2 text-sm text-muted-foreground">
                          No matches
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AgentMetricsPicker;
